/**
 * usageParts — the Usage page's pieces on the queue grammar: a numbered
 * section, a stat tile, a segmented control, a two-tone bar and the HTML
 * histogram. Each prints the class names `styles/queue.css` already defines
 * (`.sec`, `.tiles`, `.tile`, `.seg`, `.bar2`, `.legend`, `.hist`).
 */
import type { CSSProperties, ReactNode } from "react";

import { cx } from "../queue/index.js";

/** A dash for a cell with nothing honest to print. A hyphen, never an em dash. */
export const DASH = "-";

export function Quiet({ children = DASH, title }: { children?: ReactNode; title?: string }) {
  return (
    <span style={{ color: "var(--text-quiet)" }} title={title}>
      {children}
    </span>
  );
}

/** A numbered section: `01 Developers  the fact  ·····  the buttons`. */
export function Sec({
  k,
  title,
  fact,
  right,
  testId,
  children,
}: {
  k?: string;
  title: ReactNode;
  fact?: ReactNode;
  right?: ReactNode;
  testId?: string;
  children?: ReactNode;
}) {
  return (
    <section className="sec" data-testid={testId}>
      <div className="sh">
        {k && <span className="k">{k}</span>}
        <h2>{title}</h2>
        {fact != null && <span className="fact">{fact}</span>}
        {right != null && (
          <>
            <span className="sp" />
            {right}
          </>
        )}
      </div>
      {children}
    </section>
  );
}

export type TileTone = "blue" | "green" | "amber" | "quiet";

export function Tile({
  value,
  label,
  sub,
  tone,
  title,
}: {
  value: ReactNode;
  label: ReactNode;
  sub?: ReactNode;
  tone?: TileTone;
  title?: string;
}) {
  return (
    <div className="tile" title={title}>
      <span className={cx("n", tone)}>{value}</span>
      <span className="l">{label}</span>
      {sub != null && <span className="s">{sub}</span>}
    </div>
  );
}

export function Tiles({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <div className="tiles" data-testid={testId}>
      {children}
    </div>
  );
}

/** "112 / 250": a tile figure with its denominator in the quiet voice. */
export function OfTotal({ n, of }: { n: ReactNode; of: ReactNode }) {
  return (
    <>
      {n} <span style={{ fontSize: 13, color: "var(--text-tertiary)" }}>/ {of}</span>
    </>
  );
}

export interface SegOption<K extends string> {
  key: K;
  label: ReactNode;
  disabled?: boolean;
}

/** A segmented control: one of a few, the picked one raised. */
export function Seg<K extends string>({
  options,
  value,
  onChange,
  label,
  big,
}: {
  options: ReadonlyArray<SegOption<K>>;
  value: K | null;
  onChange?: (key: K) => void;
  label: string;
  big?: boolean;
}) {
  return (
    <span className={cx("seg", big && "big")} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          className={cx(value === option.key && "on")}
          aria-pressed={value === option.key}
          disabled={option.disabled}
          onClick={() => onChange?.(option.key)}
        >
          {option.label}
        </button>
      ))}
    </span>
  );
}

export interface BarPart {
  share: number;
  /** A `.k-*` class, or a colour for the inline background. */
  className?: string;
  color?: string;
}

/** The thin two-tone bar under a headline. */
export function Bar2({ parts, label, testId }: { parts: BarPart[]; label: string; testId?: string }) {
  return (
    <div className="bar2" role="img" aria-label={label} data-testid={testId}>
      {parts.map((part, index) => (
        <i
          key={index}
          className={part.className}
          style={{ width: `${Math.max(0, Math.min(100, part.share))}%`, background: part.color }}
        />
      ))}
    </div>
  );
}

export function Legend({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div className="legend" style={style}>
      {children}
    </div>
  );
}

export function Key({ className, color, children }: { className?: string; color?: string; children: ReactNode }) {
  return (
    <span>
      <i className={className} style={color ? { background: color } : undefined} />
      {children}
    </span>
  );
}

export interface HistColumn {
  key: string;
  label: string;
  /** The figure over the column. */
  value: string;
  /** Bars side by side, each on the shared scale. */
  bars: Array<{ amount: number; color: string }>;
  /** The column's accessible name. */
  aria: string;
}

/** The HTML histogram: columns of paired bars on one scale, labelled below. */
export function Hist({ columns, label }: { columns: HistColumn[]; label: string }) {
  const max = Math.max(1, ...columns.flatMap((column) => column.bars.map((bar) => bar.amount)));
  return (
    <div className="hist" role="group" aria-label={label} style={{ overflowX: "auto" }}>
      {columns.map((column) => (
        <div key={column.key} className="hcol" role="img" aria-label={column.aria} title={column.aria}>
          <span className="hv">{column.value}</span>
          <div className="hb pair">
            {column.bars.map((bar, index) =>
              bar.amount > 0 ? (
                <i
                  key={index}
                  style={{ height: `${Math.max(2, Math.round((100 * bar.amount) / max))}%`, background: bar.color }}
                />
              ) : (
                <i key={index} style={{ height: 0 }} />
              ),
            )}
          </div>
          <span className="hl">{column.label}</span>
        </div>
      ))}
    </div>
  );
}

/** A quiet sentence under a table or a section. */
export function Note({ children, tone, testId }: { children: ReactNode; tone?: "honest"; testId?: string }) {
  return (
    <p
      className="fact"
      data-testid={testId}
      style={tone === "honest" ? { color: "var(--honesty)", fontFamily: "var(--body)" } : { fontFamily: "var(--body)", fontSize: 12 }}
    >
      {children}
    </p>
  );
}
